import type { LocationObjectCoords } from 'expo-location';

import {
  locationsSearchByGeoQuery,
  locationsSearchByRegion,
  locationsSearchByRestaurantNameQuery,
} from '../../queries';
import { getSearchRadiusByPlaceType } from '../../utils';
import {
  checkIfNonNullable,
  formatAndSplitLocations,
  mapRestaurantToStoreLocation,
} from './locations.helpers';

import type {
  BaseLocationRadiusConfig,
  MapBounds,
  PositionCoordinates,
} from '../../LocationSearch.types';
import type { LocationGroups } from '../../LocationSearch.types';
import type { Client } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches locations based on a restaurant name
 */
export async function searchLocationsByRestaurantName(
  params: SearchLocationsByStringParams,
): Promise<LocationGroups> {
  const { client, context } = params;
  const { currentPosition, predictionsSearchQuery } = context;

  if (!predictionsSearchQuery) {
    return { pickup: [], outpost: [] };
  }

  const query = locationsSearchByRestaurantNameQuery;

  const { data } = await query(client, { query: predictionsSearchQuery });
  const restaurantLocations = data?.searchRestaurants;

  if (!restaurantLocations) {
    throw new Error('Failed to fetch restaurant locations by string');
  }

  const locations = restaurantLocations
    .filter(checkIfNonNullable)
    .map(mapRestaurantToStoreLocation);

  return formatAndSplitLocations(locations, currentPosition);
}

export async function searchLocationsByRegion(
  params: SearchLocationsByRegionParams,
): Promise<LocationGroups> {
  const { client, context } = params;
  const { currentPosition, searchArea } = context;

  if (!searchArea) {
    return { pickup: [], outpost: [] };
  }

  const { topLeft, bottomRight } = searchArea;

  const { latitude: topLeftLat, longitude: topLeftLng } = topLeft;
  const { latitude: bottomRightLat, longitude: bottomRightLng } = bottomRight;

  const { data } = await locationsSearchByRegion(client, {
    topLeftLat,
    topLeftLng,
    bottomRightLat,
    bottomRightLng,
  });
  const restaurantLocations = data?.restaurantsByRegion;

  if (!restaurantLocations) {
    throw new Error('Failed to fetch restaurant locations by region');
  }

  const locations = restaurantLocations
    .filter(checkIfNonNullable)
    .map(mapRestaurantToStoreLocation);

  return formatAndSplitLocations(locations, currentPosition);
}

export async function searchByGeo(params: SearchLocationsByStringParams): Promise<LocationGroups> {
  const { client, context } = params;

  if (!context.baseLocation) {
    throw new Error('Base location is required');
  }

  const { baseLocation, baseLocationRadiusConfig } = context;
  const { bounds } = baseLocation;

  // If bounds are available from Google geodata, use region query instead of radius-based query
  if (bounds) {
    const { southwest, northeast } = bounds;

    const { data } = await locationsSearchByRegion(client, {
      topLeftLat: northeast.lat,
      topLeftLng: northeast.lng,
      bottomRightLat: southwest.lat,
      bottomRightLng: southwest.lng,
      sortByLat: context.baseLocation?.latitude,
      sortByLng: context.baseLocation?.longitude,
    });
    const restaurantLocations = data?.restaurantsByRegion;

    if (!restaurantLocations) {
      throw new Error('Failed to fetch restaurant locations by region');
    }

    const locations = restaurantLocations
      .filter(checkIfNonNullable)
      .map(mapRestaurantToStoreLocation);

    // Skip client-side distance sorting to preserve backend sort order
    return formatAndSplitLocations(locations, undefined);
  }

  // Fall back to radius-based query when bounds are not available
  const { latitude, longitude } = context.baseLocation;
  const types = context.baseLocation.types;

  const query = locationsSearchByGeoQuery;
  const radius = getSearchRadiusByPlaceType(types, baseLocationRadiusConfig);

  const { data } = await query(client, {
    latitude,
    longitude,
    radius,
  });
  const restaurantLocations = data?.restaurantsByLocation;

  if (!restaurantLocations) {
    throw new Error('Failed to fetch restaurant locations by geo');
  }

  const locations = restaurantLocations
    .filter(checkIfNonNullable)
    .map(mapRestaurantToStoreLocation);

  // Skip client-side distance sorting to preserve backend sort order
  return formatAndSplitLocations(locations, undefined);
}

/**
 * Fetches locations based on the current position
 */
export async function searchLocationsByCurrentPosition(
  params: SearchLocationsByCurrentPositionParams,
): Promise<{ nearbyLocations: LocationGroups }> {
  const { client, currentPositionCoordinates } = params;

  if (!currentPositionCoordinates) {
    return { nearbyLocations: { pickup: [], outpost: [] } };
  }

  const { data } = await locationsSearchByGeoQuery(client, {
    latitude: currentPositionCoordinates.latitude,
    longitude: currentPositionCoordinates.longitude,
    radius: NEARBY_LOCATIONS_RADIUS_IN_MILES,
  });
  const restaurantLocations = data?.restaurantsByLocation;

  if (!restaurantLocations) {
    return { nearbyLocations: { pickup: [], outpost: [] } };
  }

  const locations = restaurantLocations
    .filter(checkIfNonNullable)
    .map(mapRestaurantToStoreLocation);

  return {
    nearbyLocations: formatAndSplitLocations(locations, currentPositionCoordinates),
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const NEARBY_LOCATIONS_RADIUS_IN_MILES = 5;

// ─── Types ───────────────────────────────────────────────────────────────────

type SearchLocationsByStringParams = {
  client: Client;
  context: SearchLocationsByStringContext;
};

type SearchLocationsByStringContext = Readonly<{
  searchString: string;
  predictionsSearchQuery: string | undefined;
  currentPosition: PositionCoordinates | undefined;
  baseLocation:
    | {
        latitude: number;
        longitude: number;
        name: string;
        types?: readonly string[];
        bounds?: {
          southwest: { lat: number; lng: number };
          northeast: { lat: number; lng: number };
        };
      }
    | undefined;
  baseLocationRadiusConfig: BaseLocationRadiusConfig;
}>;

type SearchLocationsByCurrentPositionParams = {
  client: Client;
  currentPositionCoordinates: LocationObjectCoords | undefined;
};

type SearchLocationsByRegionParams = {
  client: Client;
  context: SearchLocationsByRegionContext;
};

type SearchLocationsByRegionContext = Readonly<{
  searchArea: MapBounds | undefined;
  currentPosition: PositionCoordinates | undefined;
  baseLocation?: BaseLocation;
}>;

type BaseLocation = {
  latitude: number;
  longitude: number;
  name: string;
  types?: readonly string[];
  bounds?: {
    southwest: { lat: number; lng: number };
    northeast: { lat: number; lng: number };
  };
};
